import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import rawData from "./data.json";

const ScatterJitterPlot = () => {
  // Calculate max value from rawData and round to nearest +5 or multiple of 10
  const calculateMaxValue = () => {
    let max = 0;
    Object.values(rawData).forEach((values) => {
      if (values.length) {
        const categoryMax = Math.max(
          ...values.map((item) => item.medicare_percentage)
        );
        if (categoryMax > max) max = categoryMax;
      }
    });

    // Round up to nearest +5 or multiple of 10
    if (max % 10 < 5) {
      return Math.ceil(max / 10) * 10; // Round to nearest multiple of 10
    } else {
      return Math.ceil((max + 5) / 10) * 10; // Round to nearest multiple of 10, ensuring it's at least +5
    }
  };

  const MAX_VALUE = calculateMaxValue();
  const [range, setRange] = useState([0, MAX_VALUE]);
  const [chart, setChart] = useState(null);
  const [isDragging, setIsDragging] = useState(null); // 'min' or 'max' or null
  const sliderRef = useRef(null);

  const variation3Config = {
    colors: ["#93C5FD", "#6EE7B7", "#FCD34D", "#C4B5FD", "#FCA5A5", "#67E8F9"],
    markerType: "circle",
    markerRadius: 5,
    jitter: { x: 0.35, y: 0 },
  };

  const getFilteredData = () => {
    const filteredMedicare = {};
    Object.keys(rawData).forEach((code) => {
      filteredMedicare[code] = rawData[code].filter(
        (item) =>
          item.medicare_percentage >= range[0] &&
          item.medicare_percentage <= range[1]
      );
    });
    return filteredMedicare;
  };

  const getMaxValue = (data) => {
    let max = 0;
    Object.values(data).forEach((values) => {
      if (values.length) {
        const categoryMax = Math.max(
          ...values.map((item) => item.medicare_percentage)
        );
        if (categoryMax > max) max = categoryMax;
      }
    });
    return max;
  };

  // Enhanced dual slider with mouse/touch event listeners
  const DualRangeSlider = () => {
    const sliderRef = useRef(null);

    const getPositionFromEvent = (e) => {
      if (!sliderRef.current) return 0;

      const rect = sliderRef.current.getBoundingClientRect();
      const clientX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      return Math.round(percentage * MAX_VALUE);
    };

    const handleMouseDown = (type) => (e) => {
      e.preventDefault();
      setIsDragging(type);

      // Initial position update
      const position = getPositionFromEvent(e);
      if (type === "min") {
        const newMin = Math.min(position, range[1] - 10);
        setRange([newMin, range[1]]);
      } else {
        const newMax = Math.max(position, range[0] + 10);
        setRange([range[0], newMax]);
      }
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const position = getPositionFromEvent(e);

      if (isDragging === "min") {
        const newMin = Math.min(position, range[1] - 10);
        setRange([newMin, range[1]]);
      } else {
        const newMax = Math.max(position, range[0] + 10);
        setRange([range[0], newMax]);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
    };

    // Add event listeners for dragging
    useEffect(() => {
      const handleGlobalMouseMove = (e) => {
        handleMouseMove(e);
      };

      const handleGlobalMouseUp = () => {
        handleMouseUp();
      };

      if (isDragging) {
        document.addEventListener("mousemove", handleGlobalMouseMove);
        document.addEventListener("mouseup", handleGlobalMouseUp);
        document.addEventListener("touchmove", handleGlobalMouseMove);
        document.addEventListener("touchend", handleGlobalMouseUp);
      }

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchmove", handleGlobalMouseMove);
        document.removeEventListener("touchend", handleGlobalMouseUp);
      };
    }, [isDragging, range]);

    const handleMinChange = (e) => {
      const value = Number(e.target.value);
      const newMin = Math.min(value, range[1] - 10);
      setRange([newMin, range[1]]);
    };

    const handleMaxChange = (e) => {
      const value = Number(e.target.value);
      const newMax = Math.max(value, range[0] + 10);
      setRange([range[0], newMax]);
    };

    const minPercent = (range[0] / MAX_VALUE) * 100;
    const maxPercent = (range[1] / MAX_VALUE) * 100;

    // Calculate tick values dynamically based on MAX_VALUE
    const getTickValues = () => {
      const ticks = [];
      const step =
        MAX_VALUE <= 1000
          ? Math.ceil(MAX_VALUE / 5)
          : Math.ceil(MAX_VALUE / 10);

      // Ensure step is a multiple of 10 for cleaner numbers
      const cleanStep = Math.ceil(step / 10) * 10;

      for (let i = 0; i <= MAX_VALUE; i += cleanStep) {
        ticks.push(i);
      }
      // Ensure we include the max value
      if (ticks[ticks.length - 1] < MAX_VALUE) {
        ticks.push(MAX_VALUE);
      }
      return ticks;
    };

    const tickValues = getTickValues();

    return (
      <div
        ref={sliderRef}
        style={{ position: "relative", height: "60px", margin: "20px 0" }}
      >
        {/* Track */}
        <div
          style={{
            position: "absolute",
            top: "30px",
            left: "0",
            right: "0",
            height: "4px",
            backgroundColor: "#e2e8f0",
            borderRadius: "3px",
          }}
        />

        {/* Active range */}
        <div
          style={{
            position: "absolute",
            top: "30px",
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
            height: "6px",
            backgroundColor: "#5d5dad",
            borderRadius: "3px",
          }}
        />

        {/* Tick marks - positioned to overlap both track and progress bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            position: "absolute",
            top: "28px", // Adjusted to overlap the track
            left: "0",
            right: "0",
            height: "10px",
            pointerEvents: "none",
          }}
        >
          {tickValues.map((tick) => (
            <div key={tick} style={{ textAlign: "center", flex: 1 }}>
              <div
                style={{
                  width: "2px",
                  height: "10px", // Taller to overlap both track and progress
                  backgroundColor: "#5d5dad",
                  margin: "0 auto 0 auto",
                  zIndex: 1, // Ensure ticks are above both track and progress
                }}
              />
            </div>
          ))}
        </div>

        {/* Hidden input sliders for accessibility */}
        <input
          type="range"
          min={0}
          max={MAX_VALUE - 10}
          value={range[0]}
          onChange={handleMinChange}
          style={{
            position: "absolute",
            top: "20px",
            left: "0",
            width: "100%",
            zIndex: 2,
            opacity: 0,
            cursor: "pointer",
          }}
          aria-label="Minimum value"
        />

        <input
          type="range"
          min={10}
          max={MAX_VALUE}
          value={range[1]}
          onChange={handleMaxChange}
          style={{
            position: "absolute",
            top: "20px",
            left: "0",
            width: "100%",
            zIndex: 2,
            opacity: 0,
            cursor: "pointer",
          }}
          aria-label="Maximum value"
        />

        {/* Custom draggable thumbs with event listeners */}
        <div
          onMouseDown={handleMouseDown("min")}
          onTouchStart={handleMouseDown("min")}
          style={{
            position: "absolute",
            top: "27px",
            left: `${minPercent}%`,
            transform: "translateX(-50%)",
            width: "15px",
            height: "15px",
            backgroundColor: "#e0e7fc",
            border: "3px solid white",
            borderRadius: "50%",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 3, // Above ticks
            cursor: "grab",
            userSelect: "none",
            touchAction: "none",
          }}
        />
        <div
          onMouseDown={handleMouseDown("max")}
          onTouchStart={handleMouseDown("max")}
          style={{
            position: "absolute",
            top: "27px",
            left: `${maxPercent}%`,
            transform: "translateX(-50%)",
            width: "15px",
            height: "15px",
            backgroundColor: "#e0e7fc",
            border: "3px solid white",
            borderRadius: "50%",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 3, // Above ticks
            cursor: "grab",
            userSelect: "none",
            touchAction: "none",
          }}
        />

        {/* Value indicators */}
        <div
          style={{
            position: "absolute",
            top: "0",
            left: `${minPercent}%`,
            transform: "translateX(-50%)",
            backgroundColor: "#5d5dad",
            color: "white",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "600",
            zIndex: 4, // Above everything
            pointerEvents: "none",
          }}
        >
          {range[0]}
        </div>
        <div
          style={{
            position: "absolute",
            top: "0",
            left: `${maxPercent}%`,
            transform: "translateX(-50%)",
            backgroundColor: "#5d5dad",
            color: "white",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "600",
            zIndex: 4, // Above everything
            pointerEvents: "none",
          }}
        >
          {range[1]}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const filteredMedicare = getFilteredData();
    const categories = Object.keys(filteredMedicare);

    const series = categories.map((cat, index) => {
      const points = filteredMedicare[cat].map((item) => ({
        x: index,
        y: item.medicare_percentage,
        negotiated: item.negotiated_rates,
        provider: item.provider_name,
      }));

      const color =
        variation3Config.colors[index % variation3Config.colors.length];

      return {
        name: cat,
        data: points,
        color: color,
        marker: {
          symbol: variation3Config.markerType,
          radius: variation3Config.markerRadius,
          fillColor: color,
          lineColor: "rgba(255, 255, 255, 0.8)",
          lineWidth: 1,
          states: {
            hover: {
              enabled: true,
              lineColor: "#FFFFFF",
              lineWidth: 3,
              animation: {
                duration: 100,
              },
            },
          },
        },
      };
    });

    const yAxisMax = Math.max(getMaxValue(filteredMedicare), range[1]);

    const chartOptions = {
      chart: {
        type: "scatter",
        zoomType: "xy",
        height: 700,
        spacing: [40, 20, 40, 20],
        borderRadius: 8,
        animation: {
          enabled: true,
          duration: 500, // Smooth animation duration
          easing: "easeOutQuad",
        },
      },
      credits: { enabled: false },
      navigation: { buttonOptions: { enabled: false } },
      legend: { enabled: false },
      title: {
        text: "Medicare Percentage - Cigna",
        style: { color: "#1E293B", fontSize: "24px", fontWeight: "700" },
        align: "left",
      },
      subtitle: {
        text: `Medicare percentage across different billing codes`,
        style: { color: "#64748B", fontSize: "14px" },
        align: "left",
      },
      xAxis: {
        categories,
        title: {
          text: "Billing Codes",
          style: { fontSize: "15px", fontWeight: "bold" },
        },
        crosshair: {
          width: 1,
          color: "#e2e8f0",
          dashStyle: "Dash",
        },
      },
      yAxis: {
        type: "linear",
        title: { text: "Medicare Percentage" },
        min: range[0],
        max: yAxisMax,
        tickInterval: Math.ceil(MAX_VALUE / 10), // Dynamic tick interval
        labels: {
          style: { color: "#64748B", fontSize: "12px", fontWeight: "500" },
        },
        gridLineColor: "#F8FAFC",
        crosshair: {
          width: 0.5,
          color: "#a0a0a0",
          dashStyle: "Dash",
        },
      },
      plotOptions: {
        scatter: {
          jitter: variation3Config.jitter,
          animation: {
            enabled: true,
            duration: 500,
          },
          states: {
            hover: {
              animation: {
                duration: 100,
              },
            },
            select: {
              animation: {
                duration: 100,
              },
            },
          },
          marker: {
            states: {
              hover: {
                enabled: true,
                animation: {
                  duration: 100,
                },
              },
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: "white",
        borderColor: "#A9A9A9",
        borderRadius: 7,
        style: { fontFamily: "'Roboto Flex'" },
        headerFormat:
          `<span style="font-size: 14px; font-weight: 600">{series.name}</span><br/>` +
          `<span style="color: #E96926;">─────</span><br/>`,
        pointFormatter: function () {
          return `<div>
                      <div style="color: #64748B; font-size: 11px;">MEDICARE %</div>
                      <div style="font-size: 16px; font-weight: 700;">
                        ${Highcharts.numberFormat(this.y, 2, ".", ",")}%
                      </div>
                    </div>
                    <div style="margin-top: 10px; color: #64748B; font-size: 11px;">NEGOTIATED RATE</div>
                    <div style="font-size: 15px; font-weight: 600; color: #1E293B;">
                      $${Highcharts.numberFormat(this.negotiated, 2, ".", ",")}
                    </div>
                    <div style="margin-top: 10px; color: #64748B; font-size: 11px;">PROVIDER</div>
                    <div style="font-size: 14px; font-weight: 600; color: "#334155;">
                      ${this.provider}
                    </div>`;
        },
        animation: true,
        hideDelay: 100,
      },
      series,
    };

    if (!chart) {
      setChart(Highcharts.chart("scatter-jitter-container", chartOptions));
    } else {
      // Use animation when updating the chart
      chart.update(chartOptions, true, true, {
        duration: 500,
      });
    }
  }, [range, chart]);

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
        }}
      >
        <div style={{ marginBottom: "10px" }}>
          <p
            style={{
              fontWeight: "bold",
              marginBottom: "15px",
              fontSize: "16px",
              color: "#1E293B",
            }}
          >
            Filter Medicare Percentage{" "}
            <span style={{ color: "#5d5dad" }}>
              {range[0]} - {range[1]}
            </span>
          </p>
        </div>

        <DualRangeSlider />
      </div>

      <div id="scatter-jitter-container" style={{ height: "700px" }} />
    </div>
  );
};

export default ScatterJitterPlot;
